// Module-level store for the outcome of the most recent AMI interval persist.
//
// persistAmiIntervals() publishes here on every COMPLETED write — success or
// failure. Skips (local id, empty series) are not completions and are never
// published, so the store only ever holds 'ok' or 'failed'.
//
// Deliberately React-free: the store is written from a plain async function in
// the persistence layer, and read by <AmiPersistStatus /> via useSyncExternalStore.

export type AmiPersistState = 'ok' | 'skipped' | 'failed';

export type AmiPersistSkipReason = 'local_id' | 'empty_series';

// Which server operation produced this result. 'save' is every write that puts
// interval rows there; 'delete' is the one that takes them away. The distinction
// exists because the two failures need opposite words in front of the user: a
// failed save means the data is only in this session, a failed delete means the
// data the user just told us to destroy is still on the server. Absent means
// 'save' — every call site that predates the delete path.
export type AmiPersistOp = 'save' | 'delete';

export interface AmiPersistResult {
  state: AmiPersistState;
  reason?: AmiPersistSkipReason;
  op?: AmiPersistOp;
  projectId: string;
  caller: string;
  sent: number;
  inserted: number;
  seq: number;
  error?: string;
  // Whether re-attempting the write could ever succeed.
  //
  // false means the failure is permanent — the project is gone from the server
  // or belongs to another user — and `retry` is deliberately absent, because
  // offering a button that can only ever fail is worse than offering none. The
  // banner's only exit in that case is to purge the local copy.
  //
  // 'ok' and 'skipped' results are always recoverable: true. Nothing is wrong
  // with them, and the field must never read as an alarm for a healthy result.
  recoverable: boolean;
  retry?: () => Promise<AmiPersistResult>;
}

// Monotonic publish counter. Incremented ONLY inside publishAmiPersistStatus, so
// a repeated failure with identical fields still yields a distinct seq — that is
// what lets an observer tell "failed again" from "never retried".
let seqCounter = 0;

let current: AmiPersistResult | null = null;

const listeners = new Set<() => void>();

/**
 * Record a completed persist attempt and notify subscribers.
 *
 * Takes the result WITHOUT a seq and assigns the next one, so callers cannot
 * mint their own. Returns the completed object — the same reference that
 * getAmiPersistStatus() will hand back until the next publish.
 */
export function publishAmiPersistStatus(
  result: Omit<AmiPersistResult, 'seq'>
): AmiPersistResult {
  const published: AmiPersistResult = { ...result, seq: ++seqCounter };
  current = published;
  listeners.forEach((listener) => listener());
  return published;
}

/**
 * Subscribe to publishes. Returns an unsubscribe function.
 */
export function subscribeAmiPersistStatus(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Read the most recent published result, or null if nothing has been published.
 *
 * MUST return a stable reference between publishes — useSyncExternalStore
 * compares snapshots by identity and would re-render forever if this built a
 * new object on every read.
 */
export function getAmiPersistStatus(): AmiPersistResult | null {
  return current;
}
